/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@TextField';
import parse from 'autosuggest-highlight/parse';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import ValidationStatus from '../../Data/ValidationStatus';
import { getErrorMessage } from '../../Helpers/validation';
import {
  clearConstraintsValidations,
  setAutoCompleteInputValue,
  setComponentValidationStatus,
  updateValue,
} from '../../Lib/redux/slices/components';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { getVisiblePageComponents } from '../Page/page.helpers';
import QuestionText from '../QuestionText/QuestionText';
import {
  getComponentValidationStatus,
  isEveryComponentValid,
} from '../__helpers__/component.helpers';
import {
  createAutoCompletePopper,
  handleSettingPlacePredictions,
  getGeocodeAddresses,
  isThisLastGoogleAddressComponentOnPage,
  loadScript,
  requestPlacePredictions,
  setGoogleAddressMappedInputs,
} from './googleAddress.helpers';
import makeLocationIconStyles from './makeLocationIconStyles';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { useAutoFocusFirstEditableComponent } from '../../Hooks/useAutoFocusFirstEditableComponent';
import { renderMustache } from '../../Helpers/mustache';

const autocompleteService = { current: null, geocoder: null };
const DEBOUNCE_TIMEOUT = 400;

const GoogleAddressInput = ({ componentId, iFrameId, text, formId }) => {
  const dispatch = useDispatch();
  const { countryCode, phoneNumberCountryCode, subcategoryId } = useSelector(
    (store) => store.formConfig
  );
  const { pages } = useSelector((store) => store.paging);
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const [userHasSelectedOption, setUserHasSelectedOption] = useState(false);
  const { currentPage } = useSelector((store) => store.navigation);
  const components = useSelector((store) => store.components);
  const questionIdToComponentIdMap = useSelector(
    (store) => store.dataMappings.mappedQuestionIdToComponentId
  );
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);
  const loaded = React.useRef(false);
  const errorMessage = getErrorMessage(component.validation?.constraints);
  const {
    autocompleteEnabled,
    addressMap,
    options,
    inputValue,
    placeholder,
    restrictFromCountry,
  } = component.input;
  const theme = useTheme();
  const locationIconStyles = makeLocationIconStyles({ theme });
  const componentStatus = getComponentValidationStatus({ component });
  const isAnswered =
    componentStatus === ValidationStatus.VALID ||
    componentStatus === ValidationStatus.INVALID;

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      // eslint-disable-next-line no-return-assign
      window.googleAddressCallback = () =>
        (window.googleAddressApiLoaded = true);
      loadScript(
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyDlIL_5XND8vdgph0U8gC9oC1e-1H6aQNo&libraries=places&callback=googleAddressCallback',
        document.querySelector('head'),
        'google-maps'
      );
    }
    loaded.current = true;
  }

  useEffect(() => {
    /**
     * Attempt Navigation: on blur of the GoogleAddressInputValue if all components on this page are valid and answered
     */
    const pageComponentIds = pages[currentPage];
    const visiblePageComponents = getVisiblePageComponents(
      pageComponentIds,
      components,
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId
    );

    if (
      isEveryComponentValid({
        components: visiblePageComponents,
        countryCode,
      }) &&
      componentStatus === ValidationStatus.VALID &&
      userHasSelectedOption &&
      isThisLastGoogleAddressComponentOnPage({ components, componentId })
    ) {
      setUserHasSelectedOption(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [
    currentPage,
    pages,
    components,
    countryCode,
    dispatch,
    componentStatus,
    componentId,
    userHasSelectedOption,
  ]);

  useEffect(() => {
    /**
     * Handle the load of the autocomplete service and cancel of a request
     */
    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();

      if (autocompleteEnabled) {
        autocompleteService.geocoder = new window.google.maps.Geocoder();
      }
    } else if (!window.google) {
      // Delay the error logging to give Google Maps API some time to load
      const timeoutId = setTimeout(() => {
        if (!window.google) {
          // eslint-disable-next-line no-console
          console.error('Google Maps API has not loaded');
        }
      }, 3000); // Wait for 3 seconds before showing the error

      // Cleanup timeout if the component unmounts or the effect reruns
      return () => clearTimeout(timeoutId);
    }
  });

  /**
   * debouncedGetPlacePredictions
   * @type {DebouncedFuncLeading<(function(*, *): void)|*> | DebouncedFunc<(function(*, *): void)|*>}
   * @description
   * - debouncing the request to get place predictions
   * - handling out of order responses by only calling the callback for the latest request i.e. to ensure that the API
   * response for "New" doesn't overwrite the API response for "New York"
   * -
   */
  const debouncedGetPlacePredictions = useMemo(() => {
    let activeRequestId = null;

    return debounce((request, callback) => {
      const currentRequestId = Date.now();
      activeRequestId = currentRequestId;

      requestPlacePredictions(
        request,
        autocompleteService,
        callback,
        activeRequestId,
        currentRequestId
      );
    }, DEBOUNCE_TIMEOUT);
  }, []);

  const handleInputChange = (event, newInputValue) => {
    if (event && event.type === 'click') {
      // i.e. if the user has selected an option from the dropdown not changed the input value
      const newInputValueInOptions = options.some(
        (option) => option.description === newInputValue
      );

      if (newInputValueInOptions) {
        setUserHasSelectedOption(true);
      }
    }

    if (event && event.type === 'change') {
      setUserHasSelectedOption(false);

      // Result formatting and filtering (not restrictive)
      const region = restrictFromCountry ? countryCode.toLowerCase() : '';

      // Restrict predictions to the country of the webform, will need to use in ISO 3166-1 Alpha-1 format
      const componentRestrictions = restrictFromCountry
        ? { country: countryCode.toLowerCase() }
        : {};

      // get and set place predictions via API - reducing the requests via memo and debounce
      debouncedGetPlacePredictions(
        { input: newInputValue, region, componentRestrictions },
        (results, status) => {
          handleSettingPlacePredictions({
            status,
            results,
            component_id: componentId,
            reduxDispatch: dispatch,
          });
        }
      );

      batch(() => {
        dispatch(
          setAutoCompleteInputValue({ componentId, value: newInputValue })
        );
        dispatch(updateValue({ componentId, value: newInputValue }));
      });
    }
  };

  const handleChange = async (event, value) => {
    if (event.type !== 'click') {
      // if the user clicked they selected the input field, so we don't want to set as selected and thus prevent navigation. An input change is where the user has selected an option.
      setUserHasSelectedOption(false);
    }

    batch(() => {
      dispatch(
        setAutoCompleteInputValue({ componentId, value: value.description })
      );
      dispatch(updateValue({ componentId, value: value.description }));
      dispatch(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });

    if (autocompleteEnabled && typeof value !== 'string') {
      const address = await getGeocodeAddresses({
        newValue: value,
        autocompleteService,
      });
      if (address) {
        setGoogleAddressMappedInputs({
          questionIdToComponentIdMap,
          address,
          addressMap,
          dispatch,
        });
      }
    }
  };

  const handleBlur = (event) => {
    event.preventDefault();
    if (inputValue === '' || inputValue === undefined) {
      return;
    }
    setUserHasSelectedOption(false);
    batch(() => {
      dispatch(setAutoCompleteInputValue({ componentId, value: inputValue }));
      dispatch(updateValue({ componentId, value: inputValue }));
      dispatch(setComponentValidationStatus({ componentId, iFrameId, formId }));
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  const handleClick = () => {
    setUserHasSelectedOption(false);
    dispatch(
      clearConstraintsValidations({ componentId: component.component.id })
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const autoCompleteRef = useRef();
  useAutoFocusFirstEditableComponent({
    componentId,
    inputRef: autoCompleteRef,
  });

  return (
    <>
      <QuestionText
        heading={renderMustache({
          template: text,
          components,
          currentComponent: component,
          iFrameId,
          formId,
        })}
      />
      <Box
        marginTop={theme.mvf.spacing.medium}
        data-testid="googleAddressContainer"
      >
        <Autocomplete
          freeSolo
          disableClearable
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.description
          }
          autoComplete
          id={`google-address-${componentId}`}
          inputValue={inputValue || ''}
          onInputChange={handleInputChange}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          options={options || []}
          isOptionEqualToValue={(option, value) => option === value}
          onBlur={handleBlur}
          PopperComponent={createAutoCompletePopper(autoCompleteRef)}
          ref={autoCompleteRef}
          renderInput={(params) => (
            <TextField
              inputProps={params.inputProps}
              inputRef={params.InputProps.ref}
              fullWidth
              type="text"
              placeholder={placeholder}
              errorMessage={errorMessage}
              onClick={handleClick}
              isAnswered={isAnswered}
              iconType="locationService"
            />
          )}
          renderOption={(props, option) => {
            const matches =
              option.structured_formatting.main_text_matched_substrings || [];
            const parts = parse(
              option.structured_formatting.main_text,
              matches.map((match) => [
                match.offset,
                match.offset + match.length,
              ])
            );

            return (
              <li {...props}>
                <Grid container alignItems="center">
                  <Grid item>
                    <LocationOnIcon css={locationIconStyles.icon} />
                  </Grid>
                  <Grid item xs>
                    {parts.map((part) => (
                      <span
                        key={part.text}
                        style={{ fontWeight: part.highlight ? 700 : 400 }}
                      >
                        {part.text}
                      </span>
                    ))}

                    <Typography variant="body2" color="textSecondary">
                      {option.structured_formatting.secondary_text}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      </Box>
    </>
  );
};

export default GoogleAddressInput;
